(() => {
  if (window.__brillareGiftSleeveSelectorLoadedV18) return;
  window.__brillareGiftSleeveSelectorLoadedV18 = true;
  console.info('[Gift Sleeve] V1.8 atomic add loaded');

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const busy = new WeakSet();

  function allSelectors() {
    return Array.from(document.querySelectorAll('[data-gift-sleeve-selector]'));
  }

  function getSafeFormId(form) {
    if (!(form instanceof HTMLFormElement)) return '';
    return String(form.getAttribute('id') || '');
  }

  function selectorForForm(form, trigger) {
    if (!form && trigger) form = trigger.form || trigger.closest('form');

    if (form) {
      const formId = getSafeFormId(form);
      const canonicalId = formId.endsWith('--alt') ? formId.slice(0, -5) : formId;
      const exact = allSelectors().find((selector) => selector.dataset.productFormId === canonicalId);
      if (exact) return exact;

      const scope = form.closest('product-info, .product__info-container, .product__info-wrapper, section');
      const scoped = scope?.querySelector('[data-gift-sleeve-selector]');
      if (scoped) return scoped;
    }

    if (trigger) {
      const scope = trigger.closest('product-info, .product__info-container, .product__info-wrapper, section');
      const scoped = scope?.querySelector('[data-gift-sleeve-selector]');
      if (scoped) return scoped;
    }

    const selectors = allSelectors();
    return selectors.length === 1 ? selectors[0] : null;
  }

  function resolveProductForm(trigger) {
    if (!trigger) return null;
    if (trigger.form instanceof HTMLFormElement) return trigger.form;

    const explicitId = trigger.getAttribute('form');
    if (explicitId) {
      const explicit = document.getElementById(explicitId);
      if (explicit instanceof HTMLFormElement) return explicit;
    }

    const closest = trigger.closest('form[action*="/cart/add"], form[data-type="add-to-cart-form"], form[id*="product-form"]');
    if (closest instanceof HTMLFormElement) return closest;

    const productForm = trigger.closest('product-form')?.querySelector('form');
    return productForm instanceof HTMLFormElement ? productForm : null;
  }

  function showError(selector, message) {
    if (!selector) return;
    const error = selector.querySelector('.gift-sleeve-selector__error');
    selector.classList.add('gift-sleeve-selector--error');
    if (error) {
      error.textContent = message;
      error.hidden = false;
    }
  }

  function clearError(selector) {
    if (!selector) return;
    const error = selector.querySelector('.gift-sleeve-selector__error');
    selector.classList.remove('gift-sleeve-selector--error');
    if (error) {
      error.textContent = '';
      error.hidden = true;
    }
  }

  function getVariantId(form) {
    return Number(form?.querySelector('input[name="id"]')?.value || 0);
  }

  function getQuantity(form) {
    if (!(form instanceof HTMLFormElement)) return 1;

    const formId = getSafeFormId(form);
    let associated = null;

    if (formId) {
      const escaped = window.CSS?.escape ? CSS.escape(formId) : formId.replace(/"/g, '\\"');
      associated = document.querySelector(`input[name="quantity"][form="${escaped}"]`);
    }

    const inside = form.querySelector('input[name="quantity"]');
    return Math.max(1, parseInt((associated || inside)?.value || '1', 10) || 1);
  }

  function collectParentProperties(form) {
    const properties = {};
    if (!(form instanceof HTMLFormElement)) return properties;

    const data = new FormData(form);
    for (const [name, value] of data.entries()) {
      const match = String(name).match(/^properties\[(.+)\]$/);
      if (!match) continue;
      properties[match[1]] = String(value);
    }
    return properties;
  }

  function getSellingPlan(form) {
    if (!(form instanceof HTMLFormElement)) return null;
    const data = new FormData(form);
    const value = data.get('selling_plan');
    return value ? Number(value) || String(value) : null;
  }

  function getCartRenderer() {
    return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
  }

  function getSectionsPayload(renderer) {
    if (!renderer || typeof renderer.getSectionsToRender !== 'function') return [];
    try {
      return renderer.getSectionsToRender().map((section) => section.id).filter(Boolean);
    } catch (_) {
      return [];
    }
  }

  function setButtonBusy(button, isBusy) {
    if (!button) return;
    if (isBusy) {
      button.setAttribute('aria-disabled', 'true');
      button.dataset.giftSleeveBusy = 'true';
      button.classList.add('loading');
    } else {
      button.removeAttribute('aria-disabled');
      delete button.dataset.giftSleeveBusy;
      button.classList.remove('loading');
    }
  }

  function prepareTransaction(form, selector) {
    const selected = selector?.querySelector('.gift-sleeve-card__input:checked:not(:disabled)');
    if (!selected) {
      return { error: 'Please choose 1 complimentary gift sleeve before adding this gift box.' };
    }

    const boxVariantId = getVariantId(form);
    const sleeveVariantId = Number(selected.dataset.sleeveId || selected.value || 0);
    const sleevePrice = Number(selected.dataset.sleevePrice || 0);

    if (!boxVariantId || !sleeveVariantId) {
      return { error: 'Could not identify the gift box or selected sleeve SKU.' };
    }

    if (sleevePrice !== 0) {
      return { error: 'Selected gift sleeve is not configured at ₹0.' };
    }

    return {
      boxVariantId,
      sleeveVariantId,
      sleeveSku: selected.dataset.sleeveSku || '',
      sleeveTitle: selected.dataset.sleeveTitle || 'Gift Sleeve',
      quantity: getQuantity(form),
      parentProperties: collectParentProperties(form),
      sellingPlan: getSellingPlan(form)
    };
  }

  function normalizeForTheme(result, boxVariantId) {
    if (!result || !Array.isArray(result.items)) return result;

    const parent = result.items.find((item) => Number(item.variant_id || item.id) === Number(boxVariantId)) || result.items[0];
    if (!parent) return result;

    // Dawn/Roar-style cart drawers commonly expect a top-level key/id even
    // though Shopify returns an `items` array for a multi-item add request.
    return {
      ...result,
      id: result.id || parent.id,
      key: result.key || parent.key,
      variant_id: result.variant_id || parent.variant_id,
      product_id: result.product_id || parent.product_id,
      title: result.title || parent.title
    };
  }

  async function atomicAdd(form, selector, trigger) {
    if (busy.has(selector)) return;

    const tx = prepareTransaction(form, selector);
    if (tx.error) {
      showError(selector, tx.error);
      return;
    }

    busy.add(selector);
    clearError(selector);
    setButtonBusy(trigger, true);

    try {
      const renderer = getCartRenderer();
      const group = `${tx.boxVariantId}:${tx.sleeveVariantId}`;
      const parentProperties = {
        ...tx.parentProperties,
        '_gift_sleeve_group': group,
        '_gift_sleeve_variant_id': String(tx.sleeveVariantId)
      };

      const parentItem = {
        id: tx.boxVariantId,
        quantity: tx.quantity,
        properties: parentProperties
      };

      if (tx.sellingPlan) parentItem.selling_plan = tx.sellingPlan;

      const payload = {
        items: [
          parentItem,
          {
            id: tx.sleeveVariantId,
            quantity: tx.quantity,
            properties: {
              '_gift_sleeve_for_variant_id': String(tx.boxVariantId),
              '_gift_sleeve_group': group,
              '_gift_sleeve_sku': tx.sleeveSku || '',
              'Complimentary sleeve': tx.sleeveTitle
            }
          }
        ]
      };

      const sections = getSectionsPayload(renderer);
      if (sections.length) {
        payload.sections = sections;
        payload.sections_url = window.location.pathname + window.location.search;
      }

      console.info('[Gift Sleeve] Atomic cart add', {
        boxVariantId: tx.boxVariantId,
        sleeveVariantId: tx.sleeveVariantId,
        quantity: tx.quantity
      });

      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.description || result.message || 'Unable to add gift box and complimentary sleeve.');
      }

      const addedItems = Array.isArray(result.items) ? result.items : [result];
      const parentAdded = addedItems.some((item) => Number(item.variant_id || item.id) === tx.boxVariantId);
      const sleeveAdded = addedItems.some((item) => Number(item.variant_id || item.id) === tx.sleeveVariantId);

      if (!parentAdded || !sleeveAdded) {
        throw new Error('Shopify did not return both the gift box and complimentary sleeve.');
      }

      const themeResult = normalizeForTheme(result, tx.boxVariantId);

      if (renderer && typeof renderer.renderContents === 'function' && themeResult.sections) {
        renderer.renderContents(themeResult);
        if (typeof renderer.open === 'function') renderer.open();
      }

      document.dispatchEvent(new CustomEvent('gift-sleeve:added', {
        detail: {
          group,
          boxVariantId: tx.boxVariantId,
          sleeveVariantId: tx.sleeveVariantId,
          quantity: tx.quantity
        }
      }));

      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { source: 'gift-sleeve-atomic', result: themeResult }
      }));

      console.info('[Gift Sleeve] Atomic add complete — box + sleeve rendered together');
    } catch (error) {
      console.error('[Gift Sleeve] Atomic add failed:', error);
      showError(selector, error?.message || 'Unable to add complimentary gift sleeve.');
    } finally {
      setButtonBusy(trigger, false);
      busy.delete(selector);
    }
  }

  function isAddTrigger(target) {
    if (!(target instanceof Element)) return null;
    return target.closest(
      'button[name="add"], .product-form__submit, button[type="submit"], input[type="submit"], .add-to-cart, .btn-add-cart'
    );
  }

  document.addEventListener('change', (event) => {
    const input = event.target.closest?.('.gift-sleeve-card__input');
    if (!input) return;
    clearError(input.closest('[data-gift-sleeve-selector]'));
  });

  // Capture the PDP add click before the theme. For gift-box products only,
  // replace the theme's one-item request with ONE Shopify request containing
  // both the Gift Box and selected ₹0 sleeve. This removes the old polling,
  // second cart/add request and delayed drawer refresh.
  document.addEventListener('click', (event) => {
    const trigger = isAddTrigger(event.target);
    if (!trigger) return;

    const form = resolveProductForm(trigger);
    const selector = selectorForForm(form, trigger);
    if (!selector) return; // Normal products remain 100% theme-native.

    event.preventDefault();
    event.stopImmediatePropagation();

    const selected = selector.querySelector('.gift-sleeve-card__input:checked:not(:disabled)');
    if (!selected) {
      showError(selector, 'Please choose 1 complimentary gift sleeve before adding this gift box.');
      selector.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    atomicAdd(form, selector, trigger);
  }, true);

  // Keyboard/Enter submit fallback.
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const selector = selectorForForm(form, null);
    if (!selector) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const trigger = form.querySelector('button[name="add"], .product-form__submit, button[type="submit"]');
    atomicAdd(form, selector, trigger);
  }, true);
})();
